import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { MatchedTransactionRow, StatementViewModel } from '@/api/types/providerStatements';
import {
  matchedStatementColumns,
  matchedTransactionColumns,
  type MatchedColumn,
} from './providerStatementColumns';
import type { ProviderFamily } from './config';

interface Props {
  rows: MatchedTransactionRow[];
  family: ProviderFamily;
  loading?: boolean;
  selectedKey: string | null;
  onSelect: (row: MatchedTransactionRow | null) => void;
  // Leading actions cell (the audit-trail "+" button).
  rowActions: (row: MatchedTransactionRow) => ReactNode;
  excelFileName: string;
}

const PAGE_SIZES = [5, 10, 25, 50];

type SortState = { key: string; dir: 'asc' | 'desc' } | null;

function cmp(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

// The Matched Transactions grid: a two-row grouped header (platform cols ⟷ statement
// cols), client-side paging/sort/per-column search, a leading actions column, and
// single-row selection for Unmatch. Mirrors the legacy #matched-transactions-table
// (serverSide:false, pre-sorted desc by transaction.dateOfTransaction).
export function MatchedStatementsTable({
  rows,
  family,
  loading,
  selectedKey,
  onSelect,
  rowActions,
  excelFileName,
}: Props) {
  const txCols = matchedTransactionColumns;
  const stCols = matchedStatementColumns(family);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sort, setSort] = useState<SortState>(null);
  const [colSearch, setColSearch] = useState<Record<string, string>>({});

  const rowKeyOf = (r: MatchedTransactionRow) => `${r.transaction.ticket}|${r.transaction.connectorId}`;

  // Flatten accessor by a prefixed column key ("t:<key>" / "s:<key>").
  const valueAt = (r: MatchedTransactionRow, colKey: string): string | number | null | undefined => {
    if (colKey.startsWith('t:')) {
      const c = txCols.find((x) => x.key === colKey.slice(2));
      return c?.value(r.transaction);
    }
    const c = (stCols as MatchedColumn<StatementViewModel>[]).find((x) => x.key === colKey.slice(2));
    return c?.value(r.statement);
  };
  const cellAt = (r: MatchedTransactionRow, colKey: string): string => {
    if (colKey.startsWith('t:')) {
      const c = txCols.find((x) => x.key === colKey.slice(2));
      return c ? c.cell(r.transaction) : '';
    }
    const c = (stCols as MatchedColumn<StatementViewModel>[]).find((x) => x.key === colKey.slice(2));
    return c ? c.cell(r.statement) : '';
  };

  const allKeys = [...txCols.map((c) => `t:${c.key}`), ...stCols.map((c) => `s:${c.key}`)];

  const filtered = useMemo(() => {
    const active = Object.entries(colSearch).filter(([, v]) => v.trim());
    if (!active.length) return rows;
    return rows.filter((r) =>
      active.every(([key, v]) => cellAt(r, key).toLowerCase().includes(v.trim().toLowerCase())),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, colSearch]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const arr = [...filtered].sort((a, b) => cmp(valueAt(a, sort.key), valueAt(b, sort.key)));
    if (sort.dir === 'desc') arr.reverse();
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pageCount - 1);
  const start = current * pageSize;
  const pageRows = sorted.slice(start, start + pageSize);

  const pageNums = useMemo<(number | 'ellipsis')[]>(() => {
    const last = pageCount - 1;
    const DELTA = 2;
    let lo = current - DELTA;
    let hi = current + DELTA;
    if (lo < 0) { hi += -lo; lo = 0; }
    if (hi > last) { lo -= hi - last; hi = last; }
    lo = Math.max(0, lo);
    hi = Math.min(last, hi);
    const out: (number | 'ellipsis')[] = [];
    if (lo > 0) { out.push(0); if (lo > 1) out.push('ellipsis'); }
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < last) { if (hi < last - 1) out.push('ellipsis'); out.push(last); }
    return out;
  }, [current, pageCount]);

  function toggleSort(key: string) {
    setPage(0);
    setSort((s) => (!s || s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null));
  }

  function exportCsv() {
    const head = ['', ...txCols.map((c) => c.title), ...stCols.map((c) => c.title)];
    const lines = [head.map((h) => `"${h.replace(/"/g, '""')}"`).join(',')];
    sorted.forEach((r) => {
      const cells = ['', ...allKeys.map((k) => cellAt(r, k))];
      lines.push(cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${excelFileName}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const totalCols = 1 + txCols.length + stCols.length;

  const renderHeaderCell = (prefix: 't' | 's', c: MatchedColumn<unknown>) => {
    const key = `${prefix}:${c.key}`;
    const sorted = sort?.key === key;
    return (
      <th
        key={key}
        className={c.className}
        style={{ cursor: 'pointer' }}
        onClick={() => toggleSort(key)}
      >
        {c.title}
        {sorted && <i className={`ms-1 fe ${sort!.dir === 'asc' ? 'fe-chevron-up' : 'fe-chevron-down'}`} />}
      </th>
    );
  };

  const renderSearchCell = (prefix: 't' | 's', c: MatchedColumn<unknown>) => {
    const key = `${prefix}:${c.key}`;
    return (
      <th key={key} className={c.className}>
        <input
          type="search"
          className="form-control form-control-sm"
          value={colSearch[key] ?? ''}
          onChange={(e) => {
            setColSearch((prev) => ({ ...prev, [key]: e.target.value }));
            setPage(0);
          }}
        />
      </th>
    );
  };

  return (
    <div className="card custom-card mb-0">
      <div className="card-body p-0">
        <div className="finkitDatatable">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1">
            <label className="d-inline-flex align-items-center gap-1 mb-0 small text-nowrap">
              Show
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              >
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              entries
            </label>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              title="Export to Excel"
              onClick={exportCsv}
              disabled={!total}
            >
              <i className="fa fa-download" />
            </button>
          </div>

          <div className="table-responsive finkit-table-scroll">
            <table className="table dataTable table-striped table-hover text-nowrap mb-0 w-100">
              <thead>
                <tr>
                  <th rowSpan={2} style={{ width: 48 }} />
                  <th colSpan={txCols.length} className="text-center ps-group-sep">
                    Matched Provider transactions captured from trading platform
                  </th>
                  <th colSpan={stCols.length} className="text-center">
                    Matched Provider transactions captured from statement upload
                  </th>
                </tr>
                <tr>
                  {txCols.map((c) => renderHeaderCell('t', c as MatchedColumn<unknown>))}
                  {stCols.map((c) => renderHeaderCell('s', c as MatchedColumn<unknown>))}
                </tr>
                <tr className="finkit-dt-search-row">
                  <th />
                  {txCols.map((c) => renderSearchCell('t', c as MatchedColumn<unknown>))}
                  {stCols.map((c) => renderSearchCell('s', c as MatchedColumn<unknown>))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={totalCols} className="text-center py-4">
                      <i className="fa fa-spinner fa-spin me-1" />
                      Loading…
                    </td>
                  </tr>
                ) : pageRows.length ? (
                  pageRows.map((r) => {
                    const key = rowKeyOf(r);
                    const selected = selectedKey === key;
                    return (
                      <tr
                        key={key}
                        className={`cursor-pointer${selected ? ' ps-selected table-active' : ''}`}
                        onClick={() => onSelect(selected ? null : r)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>{rowActions(r)}</td>
                        {txCols.map((c) => (
                          <td key={`t:${c.key}`} className={c.className}>
                            {c.cell(r.transaction)}
                          </td>
                        ))}
                        {stCols.map((c) => (
                          <td key={`s:${c.key}`} className={c.className}>
                            {c.cell(r.statement)}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={totalCols} className="text-center text-muted py-4">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-2 py-1">
            <div className="dt-info small text-muted">
              {total === 0
                ? 'Showing 0 to 0 of 0 entries'
                : `Showing ${start + 1} to ${Math.min(start + pageSize, total)} of ${total} entries`}
            </div>
            <nav className="dt-paging">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item${current === 0 ? ' disabled' : ''}`}>
                  <button type="button" className="page-link" title="First" onClick={() => setPage(0)}>«</button>
                </li>
                <li className={`page-item${current === 0 ? ' disabled' : ''}`}>
                  <button type="button" className="page-link" title="Previous" onClick={() => setPage(current - 1)}>‹</button>
                </li>
                {pageNums.map((p, i) =>
                  p === 'ellipsis' ? (
                    <li key={`e${i}`} className="page-item disabled"><span className="page-link">…</span></li>
                  ) : (
                    <li key={p} className={`page-item${p === current ? ' active' : ''}`}>
                      <button type="button" className="page-link" onClick={() => setPage(p)}>{p + 1}</button>
                    </li>
                  ),
                )}
                <li className={`page-item${current >= pageCount - 1 ? ' disabled' : ''}`}>
                  <button type="button" className="page-link" title="Next" onClick={() => setPage(current + 1)}>›</button>
                </li>
                <li className={`page-item${current >= pageCount - 1 ? ' disabled' : ''}`}>
                  <button type="button" className="page-link" title="Last" onClick={() => setPage(pageCount - 1)}>»</button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}
